import { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { Sidebar, type Route } from './components/Sidebar';
import { Icon } from './components/Icon';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { Dashboard } from './screens/Dashboard';
import { CaseList } from './screens/CaseList';
import { CaseDetail } from './screens/CaseDetail';
import { TriggerAudit } from './screens/TriggerAudit';
import { Runs } from './screens/Runs';
import { Configuration } from './screens/Configuration';
import { ExceptionLedger } from './screens/ExceptionLedger';
import { LegacyClients } from './screens/LegacyClients';
import { ZipLookup } from './screens/ZipLookup';
import { NotificationsLog } from './screens/NotificationsLog';
import { Login } from './screens/Login';
import { ResetPassword } from './screens/ResetPassword';
import { AdminUsers } from './screens/AdminUsers';
import { SystemHealth } from './screens/SystemHealth';
import { useAuth } from './auth/AuthContext';
import { useCases, adaptApiCase, type ApiCase } from './data/useCases';
import { api, ApiError } from './lib/api';
import type { Case } from './data/sampleData';
import type { AuditRunFilter } from './screens/CaseList';
import { FbcHome } from './screens/FbcHome';

export function App() {
  const { user, loading } = useAuth();

  useEffect(() => {
    document.documentElement.dataset.density = 'comfortable';
    document.documentElement.dataset.flagStyle = 'subtle';
    document.documentElement.dataset.theme = 'light';
  }, []);

  // No router yet; just look at pathname on mount. /reset?token=… is the
  // set-password link amada-auth emails. /setup was the old local invite
  // link: those are dead, so send it to the normal sign-in.
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';

  if (path === '/setup' || (path === '/reset' && !token)) {
    window.history.replaceState(null, '', '/');
  } else if (path === '/reset') {
    return <ResetPassword token={token} />;
  }

  if (loading) {
    return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
  }

  if (!user) {
    return <Login />;
  }

  // Decision 0007: FBC accounts get their own screen and nothing else.
  if (user.role === 'fbc') return <FbcHome />;

  return <AuthedApp />;
}

function AuthedApp() {
  const { user, logout } = useAuth();
  // FBC accounts never reach here (App routes them to FbcHome).
  const staffRole: 'admin' | 'reviewer' = user!.role === 'admin' ? 'admin' : 'reviewer';
  // ?case=N in the URL → standalone full-page view of just that case (used
  // by "Open in new tab" + Copy URL). Otherwise route normally; openCaseId
  // (when set in-app) opens the side drawer over the current page.
  const initialUrlCase = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('case')
    : null;
  const [route, setRoute] = useState<Route>(initialUrlCase ? 'case' : 'dashboard');
  const [openCaseId, setOpenCaseId] = useState<string | null>(
    initialUrlCase ? `CASE-${initialUrlCase.replace(/^CASE-/, '')}` : null,
  );
  // The ?case=N link has done its job once read: clear it from the address
  // bar so a refresh opens the Dashboard, not that case again (Bettina,
  // 2026-11-03: a tab opened from "Open case" kept reopening case 265).
  useEffect(() => {
    if (initialUrlCase) window.history.replaceState(null, '', '/');
  }, []);
  const [toast, setToast] = useState<string | null>(null);

  // Queue scope: when set, the case list is server-filtered to one audit run
  // ("choose the audit you just ran, these are the results"). Set from the
  // run picker in the queue or the "View results" action on the Runs page.
  const [auditRunFilter, setAuditRunFilter] = useState<AuditRunFilter | null>(null);

  const { cases, total: casesTotal, loading: casesLoading, error: casesError, refresh } = useCases(auditRunFilter?.id);
  const queueCount = cases.filter(c => c.state === 'NEW' || c.state === 'SURFACED' || c.state === 'NOTIFIED' || c.state === 'ACKD' || c.state === 'CONFIRMED_VIOLATION').length;

  // A case opened from global search (or a ?case=N deep link) may not be in
  // the loaded window — the list is capped at 500 rows and may be run-scoped.
  // Fetch-on-miss via GET /api/cases/:id so a click is never a silent no-op
  // (the 7/1 legal-meeting bug: "I click it, nothing happens").
  const [fetchedCase, setFetchedCase] = useState<Case | null>(null);
  const [caseFetch, setCaseFetch] = useState<'idle' | 'loading' | 'notfound' | 'error'>('idle');
  useEffect(() => {
    if (!openCaseId || cases.some(c => c.id === openCaseId)) {
      setFetchedCase(null);
      setCaseFetch('idle');
      return;
    }
    let cancelled = false;
    setCaseFetch('loading');
    // A refetch of the case already open (a refresh while it stays open) keeps
    // showing it, so the page doesn't blink to "Loading case…" and lose its pop-ups.
    setFetchedCase((prev) => (prev && prev.id === openCaseId ? prev : null));
    const numeric = openCaseId.replace(/^CASE-/, '');
    api<{ case: ApiCase }>(`/api/cases/${numeric}`)
      .then((d) => { if (!cancelled) { setFetchedCase(adaptApiCase(d.case)); setCaseFetch('idle'); } })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCaseFetch(err instanceof ApiError && err.status === 404 ? 'notfound' : 'error');
      });
    return () => { cancelled = true; };
  }, [openCaseId, cases]);

  const openCase = openCaseId ? (cases.find(c => c.id === openCaseId) ?? fetchedCase) : null;

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  const transition = async (c: { id: string }, to: 'RESOLVED' | 'SURFACED' | 'DISMISSED' | 'ACKD' | 'CONFIRMED_VIOLATION', reason?: string): Promise<void> => {
    // Case ids in the UI are "CASE-<n>"; the API expects the bare numeric id.
    const numeric = c.id.replace(/^CASE-/, '');
    let resp: Response;
    try {
      resp = await fetch(`/api/cases/${numeric}/transition`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_state: to, ...(reason ? { reason } : {}) }),
      });
    } catch (err) {
      flash(`${c.id}: network error`);
      throw err;
    }
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({} as { error?: string }));
      // 409 = state machine rejected the transition. Most often this means
      // the case state changed underneath us (audit reopened it, another
      // reviewer acted on it, etc). Refresh and tell the user plainly.
      if (resp.status === 409) {
        flash(`${c.id}: case state changed since you opened it — refreshing`);
        refresh();
        throw new Error('Case state has changed since you opened it. Close this dialog and re-open the case to see the current state + available actions.');
      }
      flash(`${c.id}: ${body.error ?? `transition failed (${resp.status})`}`);
      throw new Error(body.error ?? `transition failed (${resp.status})`);
    }
    const verb = to === 'RESOLVED' ? 'RESOLVED' : to === 'SURFACED' ? 'marked Reviewed — ready to notify'
      : to === 'ACKD' ? 'marked Acknowledged' : to === 'CONFIRMED_VIOLATION' ? 'CONFIRMED VIOLATION' : 'DISMISSED for this dominant zip';
    flash(`${c.id} ${verb}`);
    setOpenCaseId(null);
    refresh();
  };

  const suppress = async (c: { id: string }, reason: string, expiresAt?: string | null): Promise<void> => {
    const numeric = c.id.replace(/^CASE-/, '');
    let resp: Response;
    try {
      resp = await fetch(`/api/cases/${numeric}/suppress`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, expires_at: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null }),
      });
    } catch (err) {
      flash(`${c.id}: network error`);
      throw err;
    }
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({} as { error?: string }));
      flash(`${c.id}: ${body.error ?? `suppress failed (${resp.status})`}`);
      throw new Error(body.error ?? `suppress failed (${resp.status})`);
    }
    flash(expiresAt
      ? `${c.id} SUPPRESSED until ${expiresAt} — audits skip this triple until then, then re-evaluate`
      : `${c.id} SUPPRESSED — future audits will skip this (client × franchisee × zip) triple`);
    setOpenCaseId(null);
    refresh();
  };

  // Reopen a closed (RESOLVED/DISMISSED) or confirmed case back to NEW. Admin-only on
  // the server; UI also hides the button for non-admins, but the server
  // double-checks. Reason is required so the audit trail captures *why*
  // the original closure was overturned.
  const reopen = async (c: { id: string; state: string }): Promise<void> => {
    const reason = window.prompt(
      `Reopen ${c.id} (currently ${c.state}) back to NEW?\n\nThis preserves the original closure record and creates a new state-history entry. Reason is required:`,
    );
    if (reason == null) return;
    if (reason.trim().length === 0) { flash('Reopen cancelled — reason is required.'); return; }
    const numeric = c.id.replace(/^CASE-/, '');
    try {
      const resp = await fetch(`/api/cases/${numeric}/transition`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_state: 'NEW', reason }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({} as { error?: string }));
        flash(`${c.id}: ${body.error ?? `reopen failed (${resp.status})`}`);
        return;
      }
      flash(`${c.id} reopened — moved back to NEW`);
      setOpenCaseId(null);
      refresh();
    } catch (err) {
      flash(`${c.id}: ${err instanceof Error ? err.message : 'network error'}`);
    }
  };

  const headerUser = {
    name: user!.name ?? user!.email,
    initials: (user!.name ?? user!.email).split(/\s|@/).map(s => s[0]).slice(0, 2).join('').toUpperCase(),
    role: user!.role === 'admin' ? 'Admin · AFI Ops' : 'Reviewer · AFI Ops',
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ImpersonationBanner />
      <Header
        user={headerUser}
        onLogout={logout}
        // Search results open full-page: the searched case may not be in the
        // loaded queue window, and the full-page route has loading/not-found
        // states (the drawer is reserved for in-queue row clicks).
        onOpenCase={(caseId) => { setOpenCaseId(caseId); setRoute('case'); }}
        onNavRuns={() => setRoute('runs')}
      />
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Sidebar active={route} onNav={setRoute} queueCount={queueCount} role={staffRole} />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {casesError && (route === 'dashboard' || route === 'queue') && (
            <div style={{ padding: 24, color: 'var(--danger)' }}>
              Could not load cases: {casesError} <button className="btn btn-sm" onClick={refresh} style={{ marginLeft: 8 }}>Retry</button>
            </div>
          )}
          {casesLoading && (route === 'dashboard' || route === 'queue') && (
            <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading cases…</div>
          )}
          {!casesLoading && !casesError && route === 'dashboard' && (
            <Dashboard
              cases={cases}
              onOpen={setOpenCaseId}
              onTrigger={() => setRoute('trigger')}
              onJumpToQueue={() => setRoute('queue')}
            />
          )}
          {!casesLoading && !casesError && route === 'queue' && (
            <CaseList
              cases={cases}
              totalCases={casesTotal}
              onOpen={setOpenCaseId}
              onTriggerAudit={() => setRoute('trigger')}
              auditRunFilter={auditRunFilter}
              onSetAuditRunFilter={setAuditRunFilter}
            />
          )}
          {route === 'trigger' && (
            <TriggerAudit
              onSubmit={() => {
                // TriggerAudit POSTs /api/audit-jobs internally and the active-job
                // banner picks up progress. We just navigate back to the queue.
                flash('Audit job queued · check the banner for progress');
                setRoute('queue');
                setTimeout(refresh, 8000);
              }}
              onCancel={() => setRoute('dashboard')}
            />
          )}
          {route === 'runs' && (
            <Runs onViewResults={(f) => { setAuditRunFilter(f); setRoute('queue'); }} />
          )}
          {route === 'ledger' && <ExceptionLedger role={staffRole} />}
          {route === 'legacy' && <LegacyClients />}
          {route === 'ziplookup' && <ZipLookup />}
          {route === 'notices' && <NotificationsLog />}
          {route === 'admin-users' && <AdminUsers />}
          {route === 'admin-system' && <SystemHealth />}
          {route === 'admin-config' && <Configuration />}
          {route === 'case' && openCase && (
            <CaseDetail
              caseObj={openCase}
              userRole={staffRole}
              display="page"
              onClose={() => { setRoute('queue'); setOpenCaseId(null); }}
              onResolve={(reason) => transition(openCase, 'RESOLVED', reason)}
              onTransition={(to, reason) => transition(openCase, to, reason)}
              onDismiss={(reason) => transition(openCase, 'DISMISSED', reason)}
              onSuppress={(reason, expiresAt) => suppress(openCase, reason, expiresAt)}
              onReopen={() => reopen(openCase)}
              onCaseChanged={() => { setRoute('queue'); setOpenCaseId(null); refresh(); }}
              onCaseRefresh={refresh}
            />
          )}
          {route === 'case' && !openCase && (
            <div style={{ padding: 40, textAlign: 'center' }} className="muted">
              {caseFetch === 'notfound' ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Case not found</div>
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    {openCaseId ? `${openCaseId} doesn't exist (it may have been removed).` : 'No case selected.'}
                  </div>
                  <button className="btn" style={{ marginTop: 14 }} onClick={() => { setRoute('queue'); setOpenCaseId(null); }}>Back to queue</button>
                </>
              ) : caseFetch === 'error' ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Couldn&apos;t load case</div>
                  <div style={{ marginTop: 6, fontSize: 13 }}>Something went wrong fetching {openCaseId}. Try again.</div>
                  <button className="btn" style={{ marginTop: 14 }} onClick={() => { setRoute('queue'); setOpenCaseId(null); }}>Back to queue</button>
                </>
              ) : (
                'Loading case…'
              )}
            </div>
          )}
        </main>
      </div>

      {/* Drawer mode: opened from any in-app navigation (e.g. clicking a row
          in the case queue). The full-page mode above is for ?case=N URLs. */}
      {route !== 'case' && openCase && (
        <CaseDetail
          caseObj={openCase}
          userRole={staffRole}
          display="drawer"
          onClose={() => setOpenCaseId(null)}
          onResolve={(reason) => transition(openCase, 'RESOLVED', reason)}
          onTransition={(to, reason) => transition(openCase, to, reason)}
          onDismiss={(reason) => transition(openCase, 'DISMISSED', reason)}
          onSuppress={(reason, expiresAt) => suppress(openCase, reason, expiresAt)}
          onReopen={() => reopen(openCase)}
          onCaseChanged={() => { setOpenCaseId(null); refresh(); }}
          onCaseRefresh={refresh}
        />
      )}

      {toast && (
        <div className="toast-wrap">
          <div className="toast">
            <Icon name="check" size={14} color="oklch(0.7 0.15 160)" />{toast}
          </div>
        </div>
      )}
    </div>
  );
}
